import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useWarehouses } from '../../hooks/useWarehouses';
import { useCustomers } from '../../hooks/useCustomers';
import { useStock } from '../../hooks/useStock';
import { applyInventoryMovement } from '../../lib/inventoryService';
import { InventoryMovementType } from '../../types/warehouse';
import Modal from '../common/Modal';
import SearchableSelect from '../common/SearchableSelect';
import BarcodeScanner from './BarcodeScanner';
import { ArrowUpTrayIcon, TrashIcon, AdjustmentsHorizontalIcon, PlusIcon, XMarkIcon, QrCodeIcon } from '@heroicons/react/24/outline';

export type StockMovementMode = 'out' | 'scrap' | 'adjust';

const MODE: Record<StockMovementMode, { title: string; icon: React.ElementType; prefix: string; button: string; reasons: { value: string; label: string }[] }> = {
  out: {
    title: 'Készletkiadás', icon: ArrowUpTrayIcon, prefix: 'KI', button: 'Kiadás rögzítése',
    reasons: [
      { value: 'sale', label: 'Értékesítés / ügyfélnek' },
      { value: 'worksheet', label: 'Munkához / helyszínre' },
      { value: 'internal', label: 'Belső felhasználás' },
      { value: 'other', label: 'Egyéb' }
    ]
  },
  scrap: {
    title: 'Selejtezés', icon: TrashIcon, prefix: 'SE', button: 'Selejt rögzítése',
    reasons: [
      { value: 'scrap', label: 'Selejt / hibás' },
      { value: 'damaged', label: 'Sérült' },
      { value: 'expired', label: 'Lejárt' },
      { value: 'lost', label: 'Elveszett / hiány' }
    ]
  },
  adjust: {
    title: 'Készletkorrekció', icon: AdjustmentsHorizontalIcon, prefix: 'KO', button: 'Korrekció rögzítése',
    reasons: [
      { value: 'correction', label: 'Hibás rögzítés javítása' },
      { value: 'found', label: 'Előkerült' },
      { value: 'opening', label: 'Nyitókészlet' },
      { value: 'other', label: 'Egyéb' }
    ]
  }
};

interface Line { key: string; productId: string; quantity: string }

interface Props {
  isOpen: boolean;
  onClose: () => void;
  mode: StockMovementMode;
  preWarehouseId?: string;
  preProductId?: string;
  onDone?: () => void;
}

const docNumber = (prefix: string) => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${prefix}-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
};
const fmt = (n: number) => new Intl.NumberFormat('hu-HU', { maximumFractionDigits: 3 }).format(n);

/** Kiadás / selejt / korrekció – több tétel, egy bizonylatszám, egy raktár. */
const StockMovementModal: React.FC<Props> = ({ isOpen, onClose, mode, preWarehouseId, preProductId, onDone }) => {
  const { user } = useAuth();
  const { warehouses } = useWarehouses();
  const { customers } = useCustomers();
  const { rows, products } = useStock();
  const meta = MODE[mode];

  const [warehouseId, setWarehouseId] = useState('');
  const [reason, setReason] = useState(meta.reasons[0].value);
  const [customerId, setCustomerId] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const def = preWarehouseId || warehouses.find(w => (w as any).isDefault)?.id || warehouses[0]?.id || '';
    setWarehouseId(def);
    setReason(MODE[mode].reasons[0].value);
    setCustomerId(''); setNote('');
    setLines([{ key: `l${Date.now()}`, productId: preProductId || '', quantity: '' }]);
  }, [isOpen, mode, preWarehouseId, preProductId, warehouses]);

  // a választott raktár készlete termékenként
  const stockIn = useMemo(() => {
    const m = new Map<string, number>();
    rows.filter(r => r.warehouseId === warehouseId).forEach(r => m.set(r.productId, (m.get(r.productId) || 0) + r.quantity));
    return m;
  }, [rows, warehouseId]);

  const productOptions = useMemo(() => {
    const list = products.filter(p => p.type !== 'service' && p.isActive !== false);
    const withStock = mode === 'adjust' ? list : list.filter(p => (stockIn.get(p.id) || 0) > 0);
    return withStock
      .map(p => ({ value: p.id, label: `${p.name}${p.code ? ` · ${p.code}` : ''} — ${fmt(stockIn.get(p.id) || 0)} ${p.unit}` }))
      .sort((a, b) => a.label.localeCompare(b.label, 'hu'));
  }, [products, stockIn, mode]);

  const unitOf = (pid: string) => products.find(p => p.id === pid)?.unit || 'db';

  const lineError = (l: Line): string | null => {
    if (!l.productId) return null;
    const q = Number(String(l.quantity).replace(',', '.'));
    if (l.quantity === '' || isNaN(q)) return 'Adj meg mennyiséget';
    if (mode === 'adjust') return q < 0 ? 'Nem lehet negatív' : null;
    if (q <= 0) return 'Pozitív szám kell';
    const have = stockIn.get(l.productId) || 0;
    const sameProduct = lines.filter(x => x.productId === l.productId).reduce((s, x) => s + (Number(String(x.quantity).replace(',', '.')) || 0), 0);
    if (sameProduct > have) return `Csak ${fmt(have)} ${unitOf(l.productId)} van`;
    return null;
  };

  const valid = !!warehouseId && lines.some(l => l.productId) && lines.filter(l => l.productId).every(l => !lineError(l));

  const onScan = (code: string) => {
    const p = products.find(x => x.barcode === code || x.code === code);
    if (!p) { toast.error(`Ismeretlen vonalkód: ${code}`); return; }
    setLines(ls => {
      const empty = ls.find(l => !l.productId);
      const existing = ls.find(l => l.productId === p.id);
      if (existing) return ls.map(l => (l === existing ? { ...l, quantity: String((Number(l.quantity) || 0) + 1) } : l));
      if (empty) return ls.map(l => (l === empty ? { ...l, productId: p.id, quantity: '1' } : l));
      return [...ls, { key: `l${Date.now()}`, productId: p.id, quantity: '1' }];
    });
    toast.success(`${p.name} +1`, { duration: 1200 });
  };

  const submit = async () => {
    if (!valid || !user) return;
    setBusy(true);
    const number = docNumber(meta.prefix);
    const orgId = (user as any).organizationId;
    let ok = 0; const errors: string[] = [];
    for (const l of lines.filter(x => x.productId)) {
      const q = Number(String(l.quantity).replace(',', '.'));
      try {
        await applyInventoryMovement({
          productId: l.productId,
          warehouseId,
          quantity: q,
          type: mode === 'adjust' ? InventoryMovementType.STOCK_ADJUSTMENT : InventoryMovementType.STOCK_OUT,
          reason,
          ...(customerId ? { customerId } : {}),
          documentType: mode === 'out' ? 'issue' : mode === 'scrap' ? 'scrap' : 'adjustment',
          documentNumber: number,
          note: note.trim() || undefined
        }, { uid: user.uid, email: user.email, organizationId: orgId });
        ok++;
      } catch (e: any) {
        errors.push(`${products.find(p => p.id === l.productId)?.name || l.productId}: ${e?.message || 'hiba'}`);
      }
    }
    setBusy(false);
    if (errors.length === 0) { toast.success(`${meta.title}: ${ok} tétel rögzítve (${number})`); onDone?.(); onClose(); }
    else toast.error(`${ok} tétel rögzítve, ${errors.length} nem: ${errors.join('; ')}`, { duration: 8000 });
  };

  const Icon = meta.icon;
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={meta.title} size="3xl" noPadding>
      <div className="flex flex-col min-h-full">
        <div className="px-4 sm:px-6 py-4 flex-1 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">Raktár *</div>
              <select value={warehouseId} onChange={e => { setWarehouseId(e.target.value); setLines(ls => ls.map(l => ({ ...l, productId: '', quantity: '' }))); }} className="w-full border rounded-lg px-3 h-10">
                {warehouses.filter(w => w.isActive !== false).map(w => <option key={w.id} value={w.id}>{w.name}{w.code ? ` (${w.code})` : ''}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">Ok *</div>
              <div className="flex flex-wrap gap-1.5">
                {meta.reasons.map(r => (
                  <button key={r.value} type="button" onClick={() => setReason(r.value)} className={`h-8 px-2.5 rounded-md text-xs font-medium border ${reason === r.value ? 'border-primary-300 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-800' : 'border-[var(--crm-border)] text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'}`}>{r.label}</button>
                ))}
              </div>
            </div>
            {mode === 'out' && (reason === 'sale' || reason === 'worksheet') && (
              <div className="md:col-span-2">
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">Ügyfél</div>
                <SearchableSelect options={customers.map(c => ({ value: c.id, label: c.type === 'organization' ? (c.companyName || 'Cég') : [c.lastName, c.firstName].filter(Boolean).join(' ') || c.email }))} value={customerId} onChange={setCustomerId} placeholder="Opcionális – megjelenik az ügyfél idővonalán" />
              </div>
            )}
          </div>

          <section className="crm-card overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)]">
              <Icon className="w-4 h-4 text-gray-400" />
              <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Tételek</h3>
              <span className="text-[11px] text-gray-500 dark:text-gray-400">· {mode === 'adjust' ? 'a HELYES (számolt) mennyiséget add meg' : 'a levonandó mennyiség'}</span>
              <button type="button" onClick={() => setScan(true)} className="ml-auto crm-btn crm-btn-secondary h-7 text-xs"><QrCodeIcon className="w-4 h-4" />Vonalkód</button>
            </div>
            <div className="p-3 space-y-2">
              {lines.map((l, i) => {
                const err = lineError(l);
                const have = stockIn.get(l.productId) || 0;
                return (
                  <div key={l.key} className="grid grid-cols-[1fr_96px_28px] gap-2 items-start">
                    <div className="min-w-0">
                      <SearchableSelect options={productOptions} value={l.productId} onChange={v => setLines(ls => ls.map(x => (x.key === l.key ? { ...x, productId: v } : x)))} placeholder={productOptions.length ? 'Termék…' : 'Ebben a raktárban nincs készlet'} />
                      {l.productId && <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Készleten: {fmt(have)} {unitOf(l.productId)}{mode === 'adjust' && l.quantity !== '' && !isNaN(Number(l.quantity)) ? ` → ${fmt(Number(l.quantity))} (${Number(l.quantity) - have >= 0 ? '+' : ''}${fmt(Number(l.quantity) - have)})` : ''}</div>}
                      {err && <div className="text-[11px] text-rose-600 mt-0.5">{err}</div>}
                    </div>
                    <div className="flex items-center gap-1">
                      <input inputMode="decimal" value={l.quantity} onChange={e => setLines(ls => ls.map(x => (x.key === l.key ? { ...x, quantity: e.target.value } : x)))} placeholder="0" className="w-full border rounded-lg px-2 h-10 text-right tabular-nums" />
                    </div>
                    <button type="button" onClick={() => setLines(ls => (ls.length > 1 ? ls.filter(x => x.key !== l.key) : [{ ...ls[0], productId: '', quantity: '' }]))} className="h-10 inline-flex items-center justify-center text-gray-400 hover:text-rose-600" aria-label={`Tétel ${i + 1} törlése`}><XMarkIcon className="w-4 h-4" /></button>
                  </div>
                );
              })}
              <button type="button" onClick={() => setLines(ls => [...ls, { key: `l${Date.now()}`, productId: '', quantity: '' }])} className="inline-flex items-center gap-1 text-xs text-primary-600"><PlusIcon className="w-3.5 h-3.5" />Tétel hozzáadása</button>
            </div>
          </section>

          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">Megjegyzés</div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder={mode === 'scrap' ? 'Mi történt? (pl. törött a szállításnál)' : 'Opcionális'} />
          </div>
        </div>
        <div className="sticky bottom-0 bg-[var(--crm-surface)] border-t border-[var(--crm-border)] px-4 sm:px-6 py-3 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="crm-btn crm-btn-ghost mr-auto" disabled={busy}>Mégse</button>
          <button type="button" onClick={submit} disabled={!valid || busy} className={`crm-btn ${mode === 'scrap' ? 'crm-btn-danger' : 'crm-btn-primary'}`}>{busy ? 'Rögzítés…' : meta.button}</button>
        </div>
      </div>
      <BarcodeScanner isOpen={scan} onClose={() => setScan(false)} onDetected={onScan} continuous />
    </Modal>
  );
};

export default StockMovementModal;
